/**
 * @desc: 筛选条件继承工具方法
 *
 * 提供页面跳转时保存/提取可继承筛选条件的能力。
 */
import { FILTER_INHERIT_NAMESPACE } from '@intelligence/models/filterInherit';
import { normalizeFilterValueByItem } from '@intelligence/v3/filterControls/apiParams';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';

import type { IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

const getResolvedInheritedValue = (item: IFilterItemConfig, value: unknown): IFilterValues[string] | undefined => {
  const normalizedValue = normalizeFilterValueByItem(item, value);

  if (normalizedValue === undefined || normalizedValue === null) {
    return undefined;
  }

  return normalizedValue as IFilterValues[string];
};

/**
 * 在页面跳转前调用，将当前页面中标记了 inherit 的筛选条件
 * 保存到 DVA 全局状态中，供目标页面消费。
 *
 * @param pageId 当前页面的page id
 * @param items 当前页面的筛选器配置
 * @param values 当前页面的筛选器值
 * @param dispatch DVA dispatch
 */
export const saveFilterInheritValues = (
  pageId: string,
  items: IFilterItemConfig[],
  values: IFilterValues,
  dispatch: Function,
): void => {
  const inheritable = getFilterInheritValues(items, values);

  dispatch({
    type: `${FILTER_INHERIT_NAMESPACE}/saveInheritedState`,
    payload: {
      inheritedPageId: pageId,
      inheritedValues: inheritable,
    },
  });
};

export const getFilterInheritValues = (items: IFilterItemConfig[], values: IFilterValues): IFilterValues => {
  const inheritable: IFilterValues = {};

  items.forEach((item) => {
    if (isNotEmpty(item.inherit) && values[item.field] !== undefined) {
      const normalizedValue = getResolvedInheritedValue(item, values[item.field]);

      if (normalizedValue !== undefined) {
        inheritable[item.field] = normalizedValue;
      }
    }
  });

  return inheritable;
};

/**
 * 从继承值中提取匹配当前页面的筛选条件。
 *
 * 匹配规则：
 * - inherit 包含 'all' → 接受任意来源页写入的值
 * - inherit 包含 inheritedPageId → 接受该来源页写入的值
 *
 * @param items 当前页面的筛选器配置
 * @param inheritedPageId 继承来源页面标识
 * @param inheritedValues DVA model 中保存的继承值
 */
export const extractInheritedValues = (
  items: IFilterItemConfig[],
  inheritedPageId?: string,
  inheritedValues?: IFilterValues,
): IFilterValues => {
  const result: IFilterValues = {};

  items.forEach((item) => {
    const { field, inherit } = item || {};

    if (isEmpty(inherit)) {
      return;
    }

    const isMatch = inherit.includes('all') || (inheritedPageId && inherit.includes(inheritedPageId));

    if (isMatch && inheritedValues?.[field] !== undefined) {
      const normalizedValue = getResolvedInheritedValue(item, inheritedValues[field]);

      if (normalizedValue !== undefined) {
        result[field] = normalizedValue;
      }
    }
  });

  return result;
};
